import { init, reactRouterV7BrowserTracingIntegration } from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

export type SentryEnvironment =
  "development" | "production" | "staging" | "testing" | "preprod";

export interface SampleRatesConfig {
  profiles: number;
  replays: number;
  traces: number;
}

export interface SentryConfig {
  dsn: string;
  environment: SentryEnvironment;
  profilesSampleRate?: number;
  replaysSessionSampleRate?: number;
  tracesSampleRate?: number;
  allowUrls?: RegExp[];
  ignoreErrors?: RegExp[];
  tracePropagationTargets?: RegExp[];
}

const getDefaultSampleRates = (
  environment: SentryEnvironment,
): SampleRatesConfig => {
  const sampleRates: Record<SentryEnvironment, SampleRatesConfig> = {
    development: {
      profiles: 1.0,
      replays: 0,
      traces: 1.0,
    },
    production: {
      profiles: 0.000001,
      replays: 0,
      traces: 0.000001,
    },
    staging: {
      profiles: 0.5,
      replays: 0,
      traces: 0.5,
    },
    testing: {
      profiles: 0.5,
      replays: 0,
      traces: 0.5,
    },
    preprod: {
      profiles: 0.5,
      replays: 0,
      traces: 0.5,
    },
  };

  return sampleRates[environment];
};

export const initSentry = (config: SentryConfig) => {
  const {
    allowUrls,
    dsn,
    environment,
    ignoreErrors,
    profilesSampleRate,
    replaysSessionSampleRate,
    tracePropagationTargets,
    tracesSampleRate,
  } = config;

  const sampleRates = getDefaultSampleRates(environment);

  const integrations = [
    reactRouterV7BrowserTracingIntegration({
      createRoutesFromChildren,
      matchRoutes,
      useEffect,
      useLocation,
      useNavigationType,
    }),
  ];

  init({
    allowUrls,
    dsn,
    environment,
    ignoreErrors,
    integrations,
    profilesSampleRate: profilesSampleRate ?? sampleRates.profiles,
    replaysSessionSampleRate: replaysSessionSampleRate ?? sampleRates.replays,
    tracePropagationTargets,
    tracesSampleRate: tracesSampleRate ?? sampleRates.traces,
  });
};
